import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoopRoundedIcon from '@mui/icons-material/LoopRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
    Alert,
    Box,
    Chip,
    Container,
    Divider,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import useViewportMode from '../../hooks/useViewportMode';
import type { FinalizedLifecycleReplay } from '../../services/FinalizedLifecycleReplayService';

export const finalizedLifecycleCaptureBeats = [
    { id: 'finalized-assessment', label: 'Finalized assessment' },
    { id: 'development-action', label: 'Development action' },
    { id: 'ai-history', label: 'Read-only AI history' },
    { id: 'next-cycle', label: 'Next-cycle context' },
] as const;

type CaptureBeatId = typeof finalizedLifecycleCaptureBeats[number]['id'];

const DetailCard = ({ label, value }: { label: string; value: string }) => (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0, height: '100%' }}>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography fontWeight={700}>{value}</Typography>
    </Paper>
);

const FinalizedAssessment = ({ replay }: { replay: FinalizedLifecycleReplay }) => (
    <Stack spacing={2.5} data-capture-beat="finalized-assessment">
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <Box>
                    <Typography variant="h5" fontWeight={800}>{replay.TalentName}</Typography>
                    <Typography color="text.secondary">{replay.PositionName} · {replay.TemplateName}</Typography>
                </Box>
                <Chip color="success" icon={<CheckCircleOutlineRoundedIcon />} label={`Finalized ${replay.FinalizedDate}`} />
            </Stack>
        </Box>
        <Alert severity="success" icon={<VerifiedUserOutlinedIcon />}>
            Finalized by {replay.FinalizedBy}. This is the durable assessment record shown through the normal employee workflow.
        </Alert>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            {replay.Criteria.map(criterion => (
                <Paper key={criterion.Name} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                    <Stack direction="row" gap={1} justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">{criterion.Category} · {criterion.Kind}</Typography>
                            <Typography fontWeight={700}>{criterion.Name}</Typography>
                        </Box>
                        <Chip size="small" label={criterion.Rating} color="primary" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{criterion.Evidence}</Typography>
                </Paper>
            ))}
        </Box>
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">Finalized summary</Typography>
            <Typography>{replay.Summary}</Typography>
        </Paper>
    </Stack>
);

const DevelopmentAction = ({ replay }: { replay: FinalizedLifecycleReplay }) => (
    <Stack spacing={2.5} data-capture-beat="development-action">
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
            <Box>
                <Typography variant="overline" color="text.secondary">Approved development action</Typography>
                <Typography variant="h5" fontWeight={800}>{replay.DevelopmentAction.Title}</Typography>
            </Box>
            <Chip color="success" label={replay.DevelopmentAction.Status} />
        </Stack>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="body1" sx={{ fontSize: { md: '1.05rem' } }}>{replay.DevelopmentAction.Narrative}</Typography>
        </Paper>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            <DetailCard label="Duration" value={replay.DevelopmentAction.Duration} />
            <DetailCard label="Coaching cadence" value={replay.DevelopmentAction.CoachingCadence} />
        </Box>
        <Alert severity="info">The approved action stays connected to {replay.TalentName} after publication and can be reviewed over time.</Alert>
    </Stack>
);

const AiHistory = ({ replay }: { replay: FinalizedLifecycleReplay }) => (
    <Stack spacing={2.5} data-capture-beat="ai-history">
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
            <Box>
                <Typography variant="overline" color="text.secondary">Authorized supporting context</Typography>
                <Typography variant="h5" fontWeight={800}>Finalized Synergist AI history</Typography>
            </Box>
            <Chip icon={<LockOutlinedIcon />} label={replay.AiHistory.Status} />
        </Stack>
        <Alert severity="warning" icon={<VerifiedUserOutlinedIcon />}>
            {replay.AiHistory.AccessLabel}. The private supervisor transcript is not the teammate&apos;s official review content.
        </Alert>
        <Stack spacing={1.5} aria-label="Read-only finalized AI transcript">
            {replay.AiHistory.Transcript.map((message, index) => (
                <Paper
                    key={`${message.Role}-${index}`}
                    variant="outlined"
                    sx={{
                        p: 2,
                        ml: message.Role === 'Synergist AI' ? { xs: 0, md: 5 } : 0,
                        mr: message.Role === 'Northstar Trainer' ? { xs: 0, md: 5 } : 0,
                    }}
                >
                    <Stack direction="row" gap={1} alignItems="center">
                        {message.Role === 'Synergist AI' && <AutoAwesomeRoundedIcon color="primary" fontSize="small" />}
                        <Typography fontWeight={800}>{message.Role}</Typography>
                        <Chip size="small" variant="outlined" label="Retained" />
                    </Stack>
                    <Typography sx={{ mt: 1 }}>{message.Content}</Typography>
                </Paper>
            ))}
        </Stack>
        <Divider />
        <Typography fontWeight={800}>Supervisor decision history</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
            {replay.AiHistory.Decisions.map(decision => (
                <Paper key={decision.Status} variant="outlined" sx={{ p: 2 }}>
                    <Chip
                        size="small"
                        color={decision.Status === 'Accepted' ? 'success' : decision.Status === 'Rejected' ? 'default' : 'primary'}
                        label={decision.Status}
                    />
                    <Typography fontWeight={800} sx={{ mt: 1 }}>{decision.Title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{decision.Detail}</Typography>
                </Paper>
            ))}
        </Box>
    </Stack>
);

const NextCycle = ({ replay }: { replay: FinalizedLifecycleReplay }) => {
    const theme = useTheme();
    const connector = <ArrowForwardRoundedIcon color="primary" sx={{ transform: { xs: 'rotate(90deg)', md: 'none' }, flex: '0 0 auto' }} />;
    return (
        <Stack spacing={2.5} data-capture-beat="next-cycle">
            <Box>
                <Typography variant="overline" color="text.secondary">Connected context</Typography>
                <Typography variant="h5" fontWeight={800}>Continue into the next review cycle</Typography>
                <Typography color="text.secondary">The supervisor starts with useful history instead of rebuilding the story from scratch.</Typography>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems="center">
                <Paper variant="outlined" sx={{ p: 2.5, flex: 1, width: '100%', minWidth: 0 }}>
                    <HistoryRoundedIcon color="primary" />
                    <Typography variant="overline" display="block" color="text.secondary">Prior review</Typography>
                    <Typography fontWeight={800}>{replay.NextCycle.PriorReview}</Typography>
                </Paper>
                {connector}
                <Paper variant="outlined" sx={{ p: 2.5, flex: 1, width: '100%', minWidth: 0 }}>
                    <CheckCircleOutlineRoundedIcon color="success" />
                    <Typography variant="overline" display="block" color="text.secondary">Unfinished goal</Typography>
                    <Typography fontWeight={800}>{replay.NextCycle.ActiveDevelopment}</Typography>
                </Paper>
                {connector}
                <Paper variant="outlined" sx={{ p: 2.5, flex: 1, width: '100%', minWidth: 0 }}>
                    <LoopRoundedIcon color="primary" />
                    <Typography variant="overline" display="block" color="text.secondary">Newer observation</Typography>
                    <Typography fontWeight={800}>{replay.NextCycle.NewObservation.Title}</Typography>
                    <Typography variant="caption" color="text.secondary">{replay.NextCycle.NewObservation.Date}</Typography>
                </Paper>
            </Stack>
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 2, md: 3 },
                    borderColor: alpha(theme.palette.primary.main, .35),
                    backgroundColor: alpha(theme.palette.primary.main, .06),
                }}
            >
                <Typography fontWeight={800}>{replay.NextCycle.NewObservation.Description}</Typography>
                <Typography sx={{ mt: 1 }}>{replay.NextCycle.Safeguard}</Typography>
            </Paper>
            <Alert severity="success" icon={<LoopRoundedIcon />}>Review → Development → Next cycle</Alert>
        </Stack>
    );
};

const FinalizedLifecycleReplayPresentation = ({ replay }: { replay: FinalizedLifecycleReplay }) => {
    const viewport = useViewportMode();
    const [activeBeat, setActiveBeat] = useState<CaptureBeatId>('finalized-assessment');
    const content = activeBeat === 'finalized-assessment'
        ? <FinalizedAssessment replay={replay} />
        : activeBeat === 'development-action'
            ? <DevelopmentAction replay={replay} />
            : activeBeat === 'ai-history'
                ? <AiHistory replay={replay} />
                : <NextCycle replay={replay} />;

    return (
        <Container maxWidth="xl" disableGutters={viewport.isPhone} sx={{ py: { xs: 0, sm: 2, lg: 3 } }}>
            <Paper
                square={viewport.isPhone}
                data-viewport-mode={viewport.mode}
                data-replay-layout={viewport.isPhone ? 'stacked' : viewport.isTablet ? 'adaptive' : 'wide'}
                sx={{ overflow: 'hidden', minHeight: { xs: 'calc(100dvh - 88px)', sm: 'calc(100dvh - 112px)', lg: 680 } }}
            >
                <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
                        <Box>
                            <Typography variant="overline" color="primary.main">Synthetic demonstration · read-only</Typography>
                            <Typography variant="h4" fontWeight={900}>Finalized review lifecycle</Typography>
                            <Typography color="text.secondary">{replay.OrganizationName} · {replay.TalentName}</Typography>
                        </Box>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            <Chip icon={<LockOutlinedIcon />} label="No records can change" />
                            <Chip color="primary" variant="outlined" label="Northstar training fixture" />
                        </Stack>
                    </Stack>
                </Box>
                <Divider />
                <Tabs
                    value={activeBeat}
                    onChange={(_, value: CaptureBeatId) => setActiveBeat(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="Finalized lifecycle capture beats"
                    sx={{ px: { xs: 1, md: 2 } }}
                >
                    {finalizedLifecycleCaptureBeats.map((beat, index) => (
                        <Tab key={beat.id} value={beat.id} label={`${index + 1}. ${beat.label}`} />
                    ))}
                </Tabs>
                <Divider />
                <Box aria-live="polite" sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
                    {content}
                </Box>
            </Paper>
        </Container>
    );
};

export default FinalizedLifecycleReplayPresentation;
